import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Divider, Stack,
  Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import { Restaurant as RecipeIcon, MenuBook as BookIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PillTabs from 'components/shared/tabs/PillTabs';
import ProgramDocument from 'components/nutrition/ProgramDocument';
import RecipeCard from 'components/client/program/RecipeCard';
import IntakeForm from 'components/client/program/IntakeForm';
import MealPlan from 'components/meal-plan/MealPlan';
import AppBarContext from 'context/AppBarContext';
import { MY_PROGRAMS, MY_RECIPE_BOOKS } from 'api/graphql/nutrition';

function EmptyState({ icon, message }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Box sx={{ color: 'text.disabled', mb: 1 }}>{icon}</Box>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{message}</Typography>
    </Box>
  );
}
EmptyState.propTypes = { icon: PropTypes.node.isRequired, message: PropTypes.string.isRequired };

function RecommendationsTab() {
  const { t } = useTranslation(['nutrition']);
  const { data, loading } = useQuery(MY_PROGRAMS, { fetchPolicy: 'cache-and-network' });
  const programs = data?.myPrograms ?? [];

  if (loading && !data) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: 'secondary.main' }} /></Box>;
  if (programs.length === 0) return <EmptyState icon={<BookIcon sx={{ fontSize: 44 }} />} message={t('noPrograms', { ns: 'nutrition', defaultValue: 'Aún no tienes recomendaciones asignadas.' })} />;

  return (
    <Stack spacing={1.5}>
      {programs.map((p) => (
        <Accordion
          key={p.id}
          defaultExpanded={programs.length === 1}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main',
            borderLeft: '3px solid', borderLeftColor: 'gold.main', borderRadius: '14px !important',
            overflow: 'hidden', '&::before': { display: 'none' }
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
            {/* Aquí ya no va el chip General/Específico: al cliente le decía "General" sobre un
                programa que le habían asignado a él, y lo leía como "esto no es para mí". El
                alcance es información de gestión, no del cliente. */}
            <Typography sx={{ fontWeight: 800 }}>{p.title}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <ProgramDocument title={p.title} type={p.type} content={p.content} programId={p.id} hasPdf={Boolean(p.pdf_url)} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

function RecipeBooksTab() {
  const { t } = useTranslation(['nutrition']);
  const { data, loading } = useQuery(MY_RECIPE_BOOKS, { fetchPolicy: 'cache-and-network' });
  const books = data?.myRecipeBooks ?? [];

  if (loading && !data) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: 'secondary.main' }} /></Box>;
  if (books.length === 0) return <EmptyState icon={<RecipeIcon sx={{ fontSize: 44 }} />} message={t('noRecipes', { ns: 'nutrition', defaultValue: 'Aún no tienes recetarios asignados.' })} />;

  return (
    <Stack spacing={4}>
      {books.map((book) => (
        <Box key={book.id}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{book.title}</Typography>
          {book.description && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{book.description}</Typography>}
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {(book.recipes ?? []).map((r) => (
              <Grid xs={12} sm={6} md={4} key={r.id}>
                <RecipeCard recipe={r} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * "Mi Programa" del cliente: Recomendaciones · Recetario · Mis datos. Ambos modos, 375px+.
 */
function ClientProgram() {
  const { t } = useTranslation(['nutrition', 'routes']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const [tab, setTab] = useState(0);
  const pageTitle = t('clientProgram', { ns: 'routes', defaultValue: 'Mi Programa' });

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />

      {/* Toggle pill centrado (patrón MySchoolUAE): Programa · Recomendaciones · Recetario · Mis datos. */}
      <PillTabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        centered
        ariaLabel={pageTitle}
        sx={{ mb: 2.5 }}
        tabs={[
          { key: 'program', label: t('programSection', { ns: 'nutrition', defaultValue: 'Programa' }) },
          { key: 'recommendations', label: t('recommendationsTab', { ns: 'nutrition', defaultValue: 'Recomendaciones' }) },
          { key: 'recipes', label: t('recipesTab', { ns: 'nutrition', defaultValue: 'Recetario' }) },
          { key: 'myData', label: t('myDataTab', { ns: 'nutrition', defaultValue: 'Mis datos' }) }
        ]}
      />

      {/* La pestaña Programa (MealPlan) queda montada (display:none) para que el título del AppBar
          no parpadee; las demás se montan al entrar. */}
      <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
        <MealPlan />
      </Box>
      {tab === 1 && <RecommendationsTab />}
      {tab === 2 && <RecipeBooksTab />}
      {tab === 3 && <IntakeForm />}
    </>
  );
}

export default ClientProgram;
